<script setup lang="ts">
import { computed } from 'vue'

import { getClasses } from '~/js/composables/classes'
import { Analytics } from '~/js/interfaces/analytics'

import IconFa from '~/vue/_teammasters/atoms/icons/IconFa.vue'

interface Props {
    content?: string,
    disabled?: boolean
    ga?: Analytics | null,
    icon?: string | null,
    iconPosition?: 'before' | 'after' | null,
    theme?: 'light' | 'dark' | null,
    type?: 'a' | 'button' | 'div' | 'submit' | null,
    url?: string | null,
    utilities?: string
    variant?: 'small' | 'wide' | null,
}

const props = withDefaults(defineProps<Props>(), {
    content: '',
    disabled: false,
    icon: null,
    iconPosition: 'before',
    theme: null,
    type: 'button',
    url: null,
    utilities: '',
    utilitiesContent: '',
    utilitiesIcon: '',
    variant: 'small',
})

const classes = {
    _default: {
        _default: 'inline-flex items-center justify-center cursor-pointer',
        display: 'rounded-full',
        padding: 'py-2 gap-2',
        text: 'text-lg font-semibold font-primary',
        hover: 'hover:underline decoration-2 underline-offset-6',
        focus: 'focus-visible:ring-2 focus-visible:ring-offset-white dark:ring-offset-black focus-visible:ring-offset-2 focus-visible:outline-none',
        transition: 'transition-colors duration-200 transform-gpu ease-in-out',
    },
    light: {
        color: 'text-neutral-900',
        focus: 'focus-visible:ring-indigo',
    },
    dark: {
        color: 'dark:text-white',
        focus: 'dark:focus-visible:ring-yellow',
    },
}

const getType = () => {
    return props.type == 'submit' ? 'button' : props.type
}

const attributes = computed(() => {
    if (props.type === 'div') {
        return {}; // No attributes for div
    }

    return {
        title: props.ga?.label ?? props.content,
        'data-tag-category': props.ga?.category ?? '',
        'data-tag-action': props.ga?.action ?? '',
        'data-tag-label': props.ga?.label ?? '',
    }
})

</script>

<template>
    <component
        :is="getType()"
        :class="[
            getClasses(classes._default), 
            getClasses(classes.light), 
            getClasses(classes.dark), 
            variant == 'small' ? 'w-fit' : 'w-full',
            utilities
        ]"
        :href="url"
        :type="type"
        :data-theme="theme"
        v-bind="attributes"
    >
        <span :class="iconPosition == 'before' ? 'order-2' : ''">
            {{ content }}
        </span>
        <IconFa 
            v-if="icon"
            :icon="icon" 
        />
    </component>
</template>